import { Injectable } from '@angular/core';
import {
  signIn,
  signOut,
  fetchAuthSession,
  getCurrentUser,
  fetchUserAttributes
} from 'aws-amplify/auth';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';

export interface UserInfo {
  username: string;
  email: string;
  name: string;
  givenName: string;
  familyName: string;
  role: string;
  userId: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
  public readonly userInfo$: Observable<UserInfo | null> = this.userInfoSubject.asObservable();

  constructor(private readonly router: Router) {
  }
  
  loginWithOkta() {
    const clientId = environment.cognitoOAuth.clientId;
    const redirectUri = encodeURIComponent(environment.cognitoOAuth.redirectUri);
    const state = crypto.randomUUID();

    const url =
      `https://${environment.cognitoOAuth.domain}/oauth2/authorize?` +
        `identity_provider=OKTA` +
        `&redirect_uri=${redirectUri}` +
        `&response_type=code` +
        `&client_id=${clientId}` +
        `&scope=openid` +
        `&state=${state}`;

    this.router.navigateByUrl(url);
  }

 /* ===================== COGNITO (AMPLIFY V6) ===================== */
  async loginWithCognito(username: string, password: string) {
    await signIn({ username, password });
    const session = await fetchAuthSession({ forceRefresh: true });
    return {
      idToken: session.tokens?.idToken?.toString(),
      accessToken: session.tokens?.accessToken?.toString()
    };
  }

  async logoutCognito() {
    await signOut();
    this.userInfoSubject.next(null); // Clear user info on logout
    localStorage.clear(); // Clear all local storage on logout
    globalThis.location.reload(); // Use globalThis instead of window
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      // Check if we can get the current user
      const user = await getCurrentUser();
      
      // Also verify we have valid tokens
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken;
      
      return !!user && !!accessToken;
    } catch {
      return false;
    }
  }

  async getUserInfo(): Promise<UserInfo | null> {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      
      const userInfo: UserInfo = {
        username: user.username,
        email: attributes.email || '',
        name: attributes.name || attributes.given_name || user.username,
        givenName: attributes.given_name || '',
        familyName: attributes.family_name || '',
        role: attributes['custom:role'] || 'Supplier',
        userId: user.userId
      };
      
      this.userInfoSubject.next(userInfo); // Update the BehaviorSubject
      return userInfo;
    } catch (error) {
      console.error('Error fetching user info:', error);
      this.userInfoSubject.next(null);
      return null;
    }
  }

  getInitials(name: string): string {
    if (!name) return 'U';
    const names = name.trim().split(' ');
    if (names.length === 1) {
      return names[0].substring(0, 2).toUpperCase();
    }
    return (names[0][0] + names.at(-1)![0]).toUpperCase();
  }
}